// src/pages/ChatBot/configs/config-values.ts
//
// Pure value helpers for the Configs tab's staged-edit model — no React, no
// I/O. A staged edit is keyed by config id; these functions decide equality,
// validity, the value a control should display, and whether an entry reads as
// "custom". Shared by the panel (ConfigsPanel) and the row components
// (config-rows) so both judge a staged edit identically.

import type { ConfigSurfaceEntry } from './types';

// A staged edit for one config id: an explicit new value, or a staged reset to
// the platform default (sent as resetIds on save). `null` in a `set` marks a
// locally-invalid numeric field (empty / NaN) — dirty, but not savable.
export type PendingEdit = { kind: 'set'; value: unknown } | { kind: 'reset' };
export type PendingMap = Record<string, PendingEdit>;

// Structural equality for the value shapes we deal in: primitives and the
// stringList array. Enough to detect a no-op edit without a deep-equal dep.
export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => x === b[i]);
  }
  return false;
}

// Empty/NaN numeric fields are staged as `null` so the row stays dirty and can
// show its red outline, but Save stays disabled and the value is never sent.
export function isInvalidValue(value: unknown): boolean {
  return value === null || (typeof value === 'number' && Number.isNaN(value));
}

export function isInvalidPending(edit: PendingEdit): boolean {
  return edit.kind === 'set' && isInvalidValue(edit.value);
}

export function hasOverride(entry: ConfigSurfaceEntry): boolean {
  return Object.prototype.hasOwnProperty.call(entry, 'override');
}

// The value a control should display given any staged edit.
export function displayValue(entry: ConfigSurfaceEntry, edit: PendingEdit | undefined): unknown {
  if (!edit) return entry.value;
  if (edit.kind === 'reset') return entry.default;
  return edit.value;
}

// "custom" = differs from the platform default. A staged reset is heading back
// to default, so it never reads as custom; a staged set is judged on its value;
// with no staged edit, the stored override decides.
export function isCustom(entry: ConfigSurfaceEntry, edit: PendingEdit | undefined): boolean {
  if (edit?.kind === 'reset') return false;
  if (edit?.kind === 'set') return !valuesEqual(edit.value, entry.default);
  return hasOverride(entry);
}
